import { useEffect, useRef } from "react";
import { useIsRestoring, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import * as Sentry from "@sentry/react";

import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { useOnlineStatus } from "@/offline/online-status";
import { resyncPinnedProjects } from "@/offline/sync";

const INTERVAL_MS = 10 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;

type TriggerSource = "boot" | "online-flip" | "sign-in" | "visibility" | "interval";

// The min-gap guards against spurious automatic triggers (visibility flap,
// interval ticks that land right after a fire). Explicit state-change
// triggers — boot, online-flip, sign-in — are NEVER suppressed; they each
// represent a deliberate transition that the user expects to refresh data.
const SUPPRESSIBLE_TRIGGERS = new Set<TriggerSource>(["visibility", "interval"]);

/**
 * Refresh every pinned project's records and plans when the app is
 * authenticated and online. See `specs/technical/offline-resync.spec.md`
 * for the full contract.
 *
 * Fires on:
 * - first authenticated mount with the network up ("boot")
 * - each offline → online flip ("online-flip")
 * - each fresh sign-in ("sign-in")
 * - tab becoming visible while online + authed ("visibility")
 * - every 10 min while online + authed + visible ("interval")
 *
 * A 60-second min-gap suppresses back-to-back firings across triggers.
 * Quiet while restoring the persisted query cache, while signed out, and
 * while offline. Per-trigger source is breadcrumbed to Sentry so we can
 * see in prod which triggers are doing the work.
 */
export function useResyncPinnedProjects(): void {
  const { user, isLoading } = useAuth();
  const isOnline = useOnlineStatus();
  const isRestoring = useIsRestoring();
  const queryClient = useQueryClient();
  const fetchWithAuth = useAuthenticatedFetch();

  const wasOnlineAndAuthedRef = useRef(false);
  const hasFiredRef = useRef(false);
  const prevUserIdRef = useRef<string | null>(null);
  const lastRunAtRef = useRef(0);

  useEffect(() => {
    // Wait for the persister to finish hydrating — otherwise we'd
    // invalidateQueries on caches the persister immediately overwrites,
    // which both wastes work and can flicker stale-then-live UI.
    if (isRestoring) return;

    // Sign-out (or first render before WorkOS resolves) drops the gate so
    // the next sign-in's first authenticated render triggers a fresh resync.
    // prevUserIdRef is also cleared so a same-user sign-in is attributed as
    // "sign-in", not "online-flip".
    if (isLoading || !user) {
      wasOnlineAndAuthedRef.current = false;
      prevUserIdRef.current = null;
      return;
    }

    if (!isOnline) {
      wasOnlineAndAuthedRef.current = false;
      return;
    }

    const dispatchResync = (trigger: TriggerSource): void => {
      const now = Date.now();
      if (
        SUPPRESSIBLE_TRIGGERS.has(trigger) &&
        now - lastRunAtRef.current < MIN_GAP_MS
      ) {
        Sentry.addBreadcrumb({
          category: "offline-sync",
          type: "info",
          message: "resync triggered",
          data: { trigger, suppressed: "min-gap" },
        });
        return;
      }
      lastRunAtRef.current = now;
      hasFiredRef.current = true;
      Sentry.addBreadcrumb({
        category: "offline-sync",
        type: "info",
        message: "resync triggered",
        data: { trigger },
      });
      resyncPinnedProjects({ queryClient, fetchWithAuth }).catch((err) => {
        Sentry.captureException(err, {
          tags: {
            area: "offline-sync",
            phase: "resync-effect",
            trigger,
          },
        });
      });
    };

    // Fire on the entry edge (re-arming after offline / sign-out / boot) OR
    // on an identity switch while staying online. Attribute the trigger
    // from what changed since the last fire.
    const userId = user.id;
    const shouldFire =
      !wasOnlineAndAuthedRef.current || prevUserIdRef.current !== userId;
    if (shouldFire) {
      wasOnlineAndAuthedRef.current = true;
      const trigger: TriggerSource = !hasFiredRef.current
        ? "boot"
        : prevUserIdRef.current !== userId
          ? "sign-in"
          : "online-flip";
      prevUserIdRef.current = userId;
      dispatchResync(trigger);
    }

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== "visible") return;
      dispatchResync("visibility");
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      dispatchResync("interval");
    }, INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [user, isLoading, isOnline, isRestoring, queryClient, fetchWithAuth]);
}
